'use client'

/**
 * /traffic — unified conversion-paths page for logged-in and example views.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { cn } from '@/lib/utils'
import type { JourneyFlowResult } from '@/lib/measurement/journey-flow'
import { usePageController } from '@/lib/page-controllers/use-page-controller'
import {
  trafficReducer,
  TRAFFIC_DEFAULT_STATE,
  rangeToToolVocab,
  type TrafficControllerState,
} from '@/lib/page-controllers/traffic-reducer'
import {
  EXAMPLE_FLOW,
  CHANNEL_COLOR,
  OUTCOME_COLOR,
  type ConversionFlowData,
} from '@/lib/traffic/conversion-example'
import { journeyToConversionFlow, legendForFlow } from '@/lib/traffic/journey-to-conversion-flow'
import { ConversionPathsSankey } from '@/components/traffic/ConversionPathsSankey'
import { DemoDataBadge } from '@/components/DemoDataBadge'

type RangeKey = 1 | 7 | 30 | 'all'
type Metric = 'sessions' | 'revenue'

const RANGE_OPTIONS: Array<{ label: string; key: RangeKey }> = [
  { label: '24h', key: 1 },
  { label: '7d', key: 7 },
  { label: '30d', key: 30 },
  { label: 'All time', key: 'all' },
]

const METRIC_OPTIONS: Array<{ label: string; key: Metric }> = [
  { label: 'Sessions', key: 'sessions' },
  { label: 'Revenue', key: 'revenue' },
]

const EXAMPLE_LEGEND = [
  { label: 'Search', color: CHANNEL_COLOR.search },
  { label: 'Social', color: CHANNEL_COLOR.social },
  { label: 'TV / CTV', color: CHANNEL_COLOR.tv },
  { label: 'Programmatic', color: CHANNEL_COLOR.programmatic },
  { label: 'Audio', color: CHANNEL_COLOR.audio },
  { label: 'Purchase', color: OUTCOME_COLOR.converted },
]

function rangeToParam(r: RangeKey): string {
  return r === 'all' ? 'all' : String(r)
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1000 === 0 ? 0 : 1)}K`
  return new Intl.NumberFormat('en-US').format(n)
}

function formatCents(cents: number): string {
  const value = cents / 100
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 10_000) return `$${(value / 1_000).toFixed(1)}k`
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

/** Scale example flow link values when toggling revenue mode. */
function exampleFlowForMetric(metric: Metric): ConversionFlowData {
  if (metric === 'sessions') return EXAMPLE_FLOW
  const sessionTotal = EXAMPLE_FLOW.totals.sessions
  const revenueTotal = EXAMPLE_FLOW.totals.revenueCents
  if (sessionTotal <= 0) return EXAMPLE_FLOW
  const scale = revenueTotal / sessionTotal
  return {
    ...EXAMPLE_FLOW,
    links: EXAMPLE_FLOW.links.map((l) => ({
      ...l,
      value: Math.round(l.value * scale),
    })),
  }
}

interface LiveProps {
  mode: 'live'
  initialFlow: JourneyFlowResult
  initialRange: RangeKey
}

interface ExampleProps {
  mode: 'example'
}

type Props = LiveProps | ExampleProps

export default function TrafficClient(props: Props) {
  const isExample = props.mode === 'example'

  const [pageState, dispatch] = usePageController<TrafficControllerState>(
    'traffic',
    {
      ...TRAFFIC_DEFAULT_STATE,
      range: isExample ? 7 : props.initialRange,
    },
    trafficReducer,
  )
  const { range, metric } = pageState

  const [flow, setFlow] = useState<JourneyFlowResult | null>(
    isExample ? null : props.initialFlow,
  )
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const fetchFlow = useCallback(
    async (nextRange: RangeKey, nextMetric: Metric) => {
      setLoading(true)
      setErrorMsg(null)
      try {
        const qs = new URLSearchParams({
          days: rangeToParam(nextRange),
          metric: nextMetric,
        })
        const res = await fetch(`/api/traffic/flow?${qs.toString()}`, { cache: 'no-store' })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.message ?? `Request failed (${res.status})`)
        }
        const json = (await res.json()) as JourneyFlowResult
        setFlow(json)
      } catch (err) {
        console.error('[/traffic] refresh failed:', err)
        setErrorMsg((err as Error).message ?? 'Failed to load traffic data')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (isExample) return
    void fetchFlow(range, metric)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, metric, isExample])

  const onRangeChange = (nextRange: RangeKey) => {
    if (nextRange === range) return
    dispatch('set_range', { range: rangeToToolVocab(nextRange) })
  }

  const onMetricChange = (nextMetric: Metric) => {
    if (nextMetric === metric) return
    dispatch('set_metric', { metric: nextMetric })
  }

  const conversionData = useMemo((): ConversionFlowData => {
    if (isExample) return exampleFlowForMetric(metric)
    if (!flow) {
      return { nodes: [], links: [], totals: { sessions: 0, purchases: 0, revenueCents: 0 } }
    }
    return journeyToConversionFlow(flow)
  }, [isExample, flow, metric])

  const legend = useMemo(
    () => (isExample ? EXAMPLE_LEGEND : legendForFlow(conversionData)),
    [isExample, conversionData],
  )

  const totals = conversionData.totals

  return (
    <div className="min-h-full w-full">
      <LobbyHeaderMeta>visualizer</LobbyHeaderMeta>

      <div className="w-full px-4 pb-16 pt-6 sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-text-strong">
                Visualizer
              </h1>
              {isExample && <DemoDataBadge />}
            </div>
            <p className="mt-1 max-w-[520px] text-[13.5px] text-text">
              See where buying journeys start and how they convert across every channel.
            </p>
            <div className="mt-3.5 flex flex-wrap gap-2.5">
              <Kpi label="Sessions" value={fmtCount(totals.sessions)} />
              <Kpi label="Purchases" value={fmtCount(totals.purchases)} accent="#0A8A6E" />
              <Kpi label="Revenue" value={formatCents(totals.revenueCents)} accent="#0A8A6E" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              options={METRIC_OPTIONS}
              active={metric}
              onChange={onMetricChange}
              disabled={loading && !isExample}
            />
            <ToggleGroup
              options={RANGE_OPTIONS}
              active={range}
              onChange={onRangeChange}
              disabled={loading && !isExample}
            />
          </div>
        </div>

        {errorMsg && !isExample && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
            {errorMsg}
            <button
              type="button"
              onClick={() => fetchFlow(range, metric)}
              className="ml-3 rounded border border-red-300 bg-white px-2 py-0.5 text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <div className="mt-4 w-full">
          <ConversionPathsSankey
            data={conversionData}
            legend={legend}
            loading={loading && !isExample}
            metric={metric}
          />
        </div>
      </div>
    </div>
  )
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white px-3.5 py-2 dark:border-white/[0.06] dark:bg-surface">
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#939294]">{label}</div>
      <div
        className={
          accent
            ? 'mt-0.5 font-mono text-[20px] font-extrabold tracking-[-0.02em] tabular-nums'
            : 'mt-0.5 font-mono text-[20px] font-extrabold tracking-[-0.02em] tabular-nums text-text-strong'
        }
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  )
}

function ToggleGroup<T extends string | number>({
  options,
  active,
  onChange,
  disabled,
}: {
  options: Array<{ label: string; key: T }>
  active: T
  onChange: (key: T) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-black/[0.08] bg-white p-0.5 dark:border-white/[0.08] dark:bg-surface">
      {options.map((opt) => (
        <button
          key={String(opt.key)}
          type="button"
          onClick={() => onChange(opt.key)}
          disabled={disabled}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium transition-colors',
            active === opt.key
              ? 'bg-text-strong text-white dark:text-canvas'
              : 'text-text-strong/70 hover:bg-black/[0.04]',
            disabled && 'opacity-60',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
